import type {
  AuthSession,
  CatalogItem,
  FormTemplate,
  Need,
  Priority,
} from "@/lib/api";

type NeedInput = Omit<Need, "id" | "folio" | "status" | "createdAt">;
type QueueStatus = "pending" | "syncing" | "failed" | "conflict";
interface Envelope {
  iv: ArrayBuffer;
  ciphertext: ArrayBuffer;
}
interface StoredPhoto {
  name: string;
  type: string;
  lastModified: number;
  envelope: Envelope;
}
interface StoredQueueItem {
  id: string;
  createdAt: string;
  updatedAt: string;
  status: QueueStatus;
  retryCount: number;
  lastError?: string;
  payload: Envelope;
  photo?: StoredPhoto;
}
export interface OfflineQueueItem {
  id: string;
  createdAt: string;
  updatedAt: string;
  status: QueueStatus;
  retryCount: number;
  lastError?: string;
  input: NeedInput;
  hasPhoto: boolean;
}
export interface OfflineZone {
  sectionId: number;
  downloadedAt: string;
  sections: unknown;
  settlements: unknown;
  blocks: unknown;
  catalogItems: CatalogItem[];
  formTemplates: FormTemplate[];
  priorities: Priority[];
}

const DB_NAME = "siscom-field-v1",
  DB_VERSION = 1;
let dbPromise: Promise<IDBDatabase> | undefined;
let keyPromise: Promise<CryptoKey> | undefined;
function requestResult<T>(request: IDBRequest<T>) {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}
function transactionDone(tx: IDBTransaction) {
  return new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}
function db() {
  dbPromise ??= new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains("meta"))
        database.createObjectStore("meta");
      if (!database.objectStoreNames.contains("queue"))
        database.createObjectStore("queue", { keyPath: "id" });
      if (!database.objectStoreNames.contains("zones"))
        database.createObjectStore("zones", { keyPath: "sectionId" });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
  return dbPromise;
}
function deviceKey() {
  keyPromise ??= (async () => {
    const database = await db();
    const read = database.transaction("meta").objectStore("meta");
    const existing = (await requestResult(read.get("device-key"))) as
      | CryptoKey
      | undefined;
    if (existing) return existing;
    const key = await crypto.subtle.generateKey(
      { name: "AES-GCM", length: 256 },
      false,
      ["encrypt", "decrypt"],
    );
    const tx = database.transaction("meta", "readwrite");
    const done = transactionDone(tx);
    tx.objectStore("meta").put(key, "device-key");
    await done;
    return key;
  })().catch((error) => {
    keyPromise = undefined;
    throw error;
  });
  return keyPromise;
}
async function encryptBytes(bytes: ArrayBuffer): Promise<Envelope> {
  const key = await deviceKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    bytes,
  );
  return {
    iv: iv.buffer.slice(iv.byteOffset, iv.byteOffset + iv.byteLength),
    ciphertext,
  };
}
async function decryptBytes(envelope: Envelope) {
  return crypto.subtle.decrypt(
    { name: "AES-GCM", iv: new Uint8Array(envelope.iv) },
    await deviceKey(),
    envelope.ciphertext,
  );
}
async function encryptJson(value: unknown) {
  return encryptBytes(new TextEncoder().encode(JSON.stringify(value)).buffer);
}
async function decryptJson<T>(value: Envelope) {
  return JSON.parse(new TextDecoder().decode(await decryptBytes(value))) as T;
}
export function notifyOfflineChange() {
  if (typeof window !== "undefined")
    window.dispatchEvent(new Event("siscom-offline-change"));
}

const OFFLINE_SESSION_KEY = "offline-session";
const OFFLINE_SESSION_TTL_MS = 12 * 60 * 60 * 1_000;

export async function saveOfflineSession(session: AuthSession) {
  if (
    !session.authenticated ||
    !session.user ||
    session.user.mustChangePassword
  )
    return;
  const payload = await encryptJson({
    session,
    expiresAt: Date.now() + OFFLINE_SESSION_TTL_MS,
  });
  const database = await db();
  const tx = database.transaction("meta", "readwrite");
  const done = transactionDone(tx);
  tx.objectStore("meta").put(payload, OFFLINE_SESSION_KEY);
  await done;
}

export async function readOfflineSession() {
  const database = await db();
  const stored = (await requestResult(
    database.transaction("meta").objectStore("meta").get(OFFLINE_SESSION_KEY),
  )) as Envelope | undefined;
  if (!stored) return undefined;
  const cached = await decryptJson<{
    session: AuthSession;
    expiresAt: number;
  }>(stored);
  if (cached.expiresAt <= Date.now()) {
    await clearOfflineSession();
    return undefined;
  }
  return cached.session;
}

export async function clearOfflineSession() {
  const database = await db();
  const tx = database.transaction("meta", "readwrite");
  const done = transactionDone(tx);
  tx.objectStore("meta").delete(OFFLINE_SESSION_KEY);
  await done;
}

export async function enqueueNeed(input: NeedInput, photo?: File) {
  const id = input.clientRequestId ?? crypto.randomUUID();
  const now = new Date().toISOString();
  const stored: StoredQueueItem = {
    id,
    createdAt: now,
    updatedAt: now,
    status: "pending",
    retryCount: 0,
    payload: await encryptJson({ ...input, clientRequestId: id }),
  };
  if (photo)
    stored.photo = {
      name: photo.name,
      type: photo.type,
      lastModified: photo.lastModified,
      envelope: await encryptBytes(await photo.arrayBuffer()),
    };
  const database = await db();
  const tx = database.transaction("queue", "readwrite");
  const done = transactionDone(tx);
  tx.objectStore("queue").put(stored);
  await done;
  notifyOfflineChange();
  return id;
}
async function storedQueue() {
  const database = await db();
  return (await requestResult(
    database.transaction("queue").objectStore("queue").getAll(),
  )) as StoredQueueItem[];
}
export async function listOfflineQueue(): Promise<OfflineQueueItem[]> {
  const items = await storedQueue();
  return Promise.all(
    items
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
      .map(async (item) => ({
        id: item.id,
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
        status: item.status,
        retryCount: item.retryCount,
        lastError: item.lastError,
        input: await decryptJson<NeedInput>(item.payload),
        hasPhoto: Boolean(item.photo),
      })),
  );
}
async function saveStored(item: StoredQueueItem) {
  const database = await db();
  const tx = database.transaction("queue", "readwrite");
  const done = transactionDone(tx);
  tx.objectStore("queue").put(item);
  await done;
  notifyOfflineChange();
}
async function removeStored(id: string) {
  const database = await db();
  const tx = database.transaction("queue", "readwrite");
  const done = transactionDone(tx);
  tx.objectStore("queue").delete(id);
  await done;
  notifyOfflineChange();
}
export async function retryOfflineItem(id: string) {
  const item = (await storedQueue()).find((row) => row.id === id);
  if (!item) return;
  item.status = "pending";
  item.lastError = undefined;
  item.updatedAt = new Date().toISOString();
  await saveStored(item);
}
export async function synchronizeOfflineQueue(
  processor: (input: NeedInput, photo?: File) => Promise<void>,
) {
  if (!navigator.onLine) return;
  for (const item of await storedQueue()) {
    if (item.status === "conflict") continue;
    item.status = "syncing";
    item.updatedAt = new Date().toISOString();
    await saveStored(item);
    try {
      const input = await decryptJson<NeedInput>(item.payload);
      let photo: File | undefined;
      if (item.photo)
        photo = new File(
          [await decryptBytes(item.photo.envelope)],
          item.photo.name,
          { type: item.photo.type, lastModified: item.photo.lastModified },
        );
      await processor(input, photo);
      await removeStored(item.id);
    } catch (error) {
      item.status =
        (error as { status?: number }).status === 409 ? "conflict" : "failed";
      item.retryCount += 1;
      item.lastError =
        error instanceof Error ? error.message : "No fue posible sincronizar";
      item.updatedAt = new Date().toISOString();
      await saveStored(item);
    }
  }
}
export async function downloadOfflineZone(sectionId: number) {
  const read = (url: string, message: string) =>
    fetch(url, { credentials: "same-origin" }).then((response) =>
      response.ok ? response.json() : Promise.reject(new Error(message)),
    );
  const [
    sections,
    settlements,
    blocks,
    catalogItems,
    formTemplates,
    priorities,
  ] = await Promise.all([
    read("/api/territory/sections", "No fue posible descargar secciones"),
    read("/api/territory/settlements", "No fue posible descargar colonias"),
    read(
      `/api/territory/blocks?section=${sectionId}`,
      "No fue posible descargar manzanas",
    ),
    read(
      "/api/catalogs/items?areaCode=general_services",
      "No fue posible descargar el catálogo",
    ),
    read(
      "/api/catalogs/forms?areaCode=general_services",
      "No fue posible descargar formatos",
    ),
    read("/api/priorities", "No fue posible descargar prioridades"),
  ]);
  const value: OfflineZone = {
    sectionId,
    downloadedAt: new Date().toISOString(),
    sections,
    settlements,
    blocks,
    catalogItems,
    formTemplates,
    priorities,
  };
  const payload = await encryptJson(value);
  const database = await db();
  const tx = database.transaction("zones", "readwrite");
  const done = transactionDone(tx);
  tx.objectStore("zones").put({
    sectionId,
    downloadedAt: value.downloadedAt,
    payload,
  });
  await done;
  notifyOfflineChange();
  return value;
}
export async function listOfflineZones() {
  const database = await db();
  const rows = (await requestResult(
    database.transaction("zones").objectStore("zones").getAll(),
  )) as Array<{ sectionId: number; downloadedAt: string; payload: Envelope }>;
  return rows.map(({ sectionId, downloadedAt }) => ({
    sectionId,
    downloadedAt,
  }));
}
export async function getOfflineZone(sectionId: number) {
  const database = await db();
  const row = (await requestResult(
    database.transaction("zones").objectStore("zones").get(sectionId),
  )) as { payload: Envelope } | undefined;
  return row ? decryptJson<OfflineZone>(row.payload) : undefined;
}
